<template>
  <div :class="DS_SLIDER_INPUT_CLASSES.NumberField">
    <DSBaseInput
      :id="props.id"
      v-model="numberInputValue"
      :type="DS_BASE_INPUT_TYPE.Number"
      data-cy-suffix="slider-input-number"
      :disabled="props.disabled"
      :min="props.min"
      :max="props.max"
      :step="props.step"
      :clearable="false"
      :has-error="props.hasError"
      :aria-label="props.accessibleLabel"
      :aria-invalid="props.hasError ? 'true' : undefined"
      :aria-describedby="props.describedBy"
      @update:model-value="onInput"
      @keydown.enter="commitInput"
      @focus="isFocused = true"
      @blur="onBlur"
    >
      <template #suffix>
        <DSNumberFieldStepper
          :controls="props.stepperControls"
          @step="onStep"
        />
      </template>
    </DSBaseInput>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import DSNumberFieldStepper from "@/src/design_system/form_elements/number_field/components/DSNumberFieldStepper.vue";
import { DS_SLIDER_INPUT_CLASSES } from "@/src/design_system/form_elements/slider_input/constants/classes";

interface DSSliderNumberInputProps {
  id: string;
  modelValue: number;
  disabled: boolean;
  min: number;
  max: number;
  step: number;
  hasError: boolean;
  accessibleLabel: string;
  describedBy?: string;
  stepperControls: {
    canIncrement: boolean;
    canDecrement: boolean;
  };
}

interface DSSliderNumberInputEmits {
  (e: "update:modelValue", value: number): void;
  (e: "change"): void;
  (e: "step", direction: -1 | 1): void;
}

const props = defineProps<DSSliderNumberInputProps>();
const emit = defineEmits<DSSliderNumberInputEmits>();

const isFocused = ref(false);
const numberInputValue = ref(String(props.modelValue));

watch(
  () => props.modelValue,
  value => {
    if (!isFocused.value) {
      numberInputValue.value = String(value);
    }
  }
);

function onInput(value: string | number) {
  numberInputValue.value = String(value);
  const parsedValue = Number(value);

  if (String(value).trim() && Number.isFinite(parsedValue)) {
    emit("update:modelValue", parsedValue);
  }
}

async function commitInput() {
  const parsedValue = Number(numberInputValue.value);

  if (numberInputValue.value.trim() && Number.isFinite(parsedValue)) {
    emit("update:modelValue", parsedValue);
  }
  emit("change");

  await syncWithModelValue();
}

function onBlur() {
  isFocused.value = false;
  numberInputValue.value = String(props.modelValue);
  emit("change");
}

async function onStep(direction: -1 | 1) {
  emit("step", direction);
  await syncWithModelValue();
}

async function syncWithModelValue() {
  await nextTick();
  numberInputValue.value = String(props.modelValue);
}
</script>
